import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {
  AdminEntity,
  BalanceProvider,
  BokkzAlgDataService,
  BokkzVpbDataService,
  BokkzVpbLogic,
  ClientEntity,
  CorporateTaxEntity,
  IncomeTaxEntity,
  KolommenbalansService,
  PmClient,
  VpbAangifte,
  VpbBerekening,
  VpbRekenService
} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormBuilder, FormGroup} from '@angular/forms';
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {catchError, combineLatest, filter, of, Subject, takeUntil, throwError} from "rxjs";
import {ToastService} from '@snelstart/ui-toast';
import {Event, NavigationEnd, Router} from '@angular/router';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {SharedUiLoadingModule} from '@snelstart/ui-loading';
import {CommonModule} from '@angular/common';

@Component({
  selector: 'snelstart-venture-fiscaal-feature-administratie-koppelen',
  templateUrl: './administratie-koppelen.component.html',
  styleUrls: ['./administratie-koppelen.component.scss'],
  imports: [
    SharedUiDetailsModule,
    SharedUiFormControlsModule,
    SharedUiButtonModule,
    SharedUiAlertModule,
    SharedUiLoadingModule,
    CommonModule
  ]
})
export class AdministratieKoppelenComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  public clientId!: number;
  private destroy$ = new Subject<void>();
  public vpbBerekening = {} as VpbBerekening;
  public corporateTaxEntity = {} as CorporateTaxEntity;
  public clientEntity = {} as ClientEntity;
  public sstAdminIdFg = {} as FormGroup;
  public adminEntityList = {} as AdminEntity[];
  public administrationList: SelectFormControlItem<string>[] = [];
  public showSpinner = false;
  private isSaving = false;
  public isDefAangifte ={} as boolean;
  public externalProviderId= {} as string;
  public hasExternalProvider = false;
  public loginStatus = false;

  public optionsJaNee: SelectFormControlItem<boolean>[] = [
    { value: true, label: 'Ja' },
    { value: false, label: 'Nee' },
  ];

  constructor(
    public dataVpbService: BokkzVpbDataService,
    public vpbRekenService: VpbRekenService,
    public vpbService: BokkzVpbLogic,
    public dataAlgService: BokkzAlgDataService,
    public pmClient: PmClient,
    public toastService: ToastService,
    private router: Router,
    private kolommenbalansService: KolommenbalansService,
    public fb: FormBuilder,
  ) {
    this.sstAdminIdFg = this.fb.group({
      id: null as unknown as string,
      name: null as unknown as string,
      rgs: null as unknown as string,
      inlezen: null as unknown as boolean,
      administrationId: null as unknown as string,
      providerId: null as unknown as string,
    });
  }

  onSelect(administrationId: string) {
    const selectedAdmin = this.clientEntity.adminEntities?.find(
      option => option.administrationId === administrationId);
    if (selectedAdmin && selectedAdmin.administrationId) {
      if (selectedAdmin.providerId) {
        this.hasExternalProvider=true
        this.externalProviderId=selectedAdmin.providerId
        this.checkExterneKoppeling(selectedAdmin);
      } else{
        this.hasExternalProvider=false
        this.externalProviderId=null as unknown as string
      }
      this.sstAdminIdFg.patchValue(selectedAdmin)
      this.saveForm();
    }
  }

  ngOnInit(): void {
    this.dataVpbService.isDefAangifte.pipe(takeUntil(this.destroy$)).subscribe((isDefAangifte)=> {
      this.isDefAangifte =isDefAangifte;
    })
    this.dataVpbService.vpbBerekening.pipe(takeUntil(this.destroy$)).subscribe((vpbBerekening) => {
      this.vpbBerekening = vpbBerekening;
    });
    const corporateTaxEntityObservable = this.dataVpbService.corporateTaxEntity.pipe(
        catchError((error) => {
          this.toastService.showErrorMessage('Error loading corporate tax entity data',);
          console.log(error);
          return of(null);
        }),
      );
    const clientEntityObservable = this.dataAlgService.client.pipe(
      catchError((error) => {
        this.toastService.showErrorMessage('Error loading client entity data');
        console.log(error);
        return of(null);
      }),
    );
    combineLatest([
      corporateTaxEntityObservable,
      clientEntityObservable,
    ]).subscribe(([corporateTaxEntity, clientEntity]) => {
      if (corporateTaxEntity && clientEntity) {
        this.corporateTaxEntity = corporateTaxEntity;
        this.clientEntity = clientEntity;
        if (clientEntity) {
          if (clientEntity.id) {
            this.clientId=clientEntity.id;
          }
          this.administrationList = [];
          const value = clientEntity;
          if (value.adminEntities !== undefined) {
            this.adminEntityList = value.adminEntities;
            for (let i = 0; this.adminEntityList[i]; i++) {
              if (this.adminEntityList[i].administrationId) {
                const admininistration = {
                  value: this.adminEntityList[i].administrationId!.toString(),
                  label: this.adminEntityList[i].name!.toString(),
                };
                this.administrationList.push(admininistration);
              }
            }
          }
        }
        if (corporateTaxEntity.aangifte) {
          if (this.corporateTaxEntity.aangifte?.balansEnResultatenrekening.administratieId !== undefined) {
            const id= this.corporateTaxEntity.aangifte?.balansEnResultatenrekening.administratieId;
            const gekoppeldeAdmin = this.adminEntityList.find((item) => item.administrationId?.toString() === id);
            if (gekoppeldeAdmin !== undefined) {
              if (gekoppeldeAdmin.providerId) {
                this.hasExternalProvider=this.checkExterneKoppeling(gekoppeldeAdmin);
              }
              this.sstAdminIdFg.patchValue({
                  inlezen: true,
                  name: gekoppeldeAdmin.name,
                  administrationId: gekoppeldeAdmin.administrationId,
                  providerId: gekoppeldeAdmin.providerId,
                  rgs: gekoppeldeAdmin.rgs },
                { emitEvent: false });
            }
          }
        }
      } else {
        // Handle the case where one or both observables returned null
        this.toastService.showErrorMessage('Some data could not be loaded');
      }
    });
    this.router.events
      .pipe(filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd,))
      .subscribe((event: NavigationEnd) => {this.handleUrlChange(event.urlAfterRedirects);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private hasUnsavedChanges(): boolean {
    return this.sstAdminIdFg.dirty
  }


  handleUrlChange(newUrl: string) {
    if (!this.isSaving && this.hasUnsavedChanges()) {
      this.isSaving = true;
      this.vpbService.postCorporateTaxEntity(this.corporateTaxEntity, false,false);
      this.isSaving = false;
    }
  }

  checkData() {
    if (this.sstAdminIdFg.get('inlezen')?.value === false) {
      this.corporateTaxEntity.sstAdminId = undefined;
      this.sstAdminIdFg.patchValue({
        inlezen: false,
        administrationId: undefined,
        name: undefined,
      },{emitEvent:false});
    }
  }

  getImportAdmin() {
    if (this.corporateTaxEntity.aangifte?.balansEnResultatenrekening.administratieId) {
      if (this.hasExternalProvider) {
        this.executeBalanceBaseProvider(this.corporateTaxEntity, false ,this.corporateTaxEntity.aangifte.balansEnResultatenrekening.administratieId)
      } else {
        this.kolommenbalansService.getUpdatedAangifteMetAdministratie(this.corporateTaxEntity.aangifte.balansEnResultatenrekening.administratieId,
          String(this.clientEntity.sstRelationId),
          this.corporateTaxEntity, false,this.clientEntity.sstPrimaryAdminId!).then((entity) => {
          if (entity) {
            if (this.isDefAangifte) {
              this.dataVpbService.updateCorporateTaxEntity(entity as CorporateTaxEntity);
            }
            else {
              this.removeBalansActivaPassiva(entity as CorporateTaxEntity);
            }
            setTimeout(() => {
              this.vpbRekenService
                .getCorporateTaxCalculation(entity.aangifte)
                .subscribe((vpbBerekening) => {
                  this.dataVpbService.updateVpbBerekening(vpbBerekening);
                });
              this.toastService.showSuccessMessage('De administratie is succesvol ingelezen in de aangifte.',);
            }, 250);
          } else {
            this.toastService.showErrorMessage('Het is helaas niet gelukt de administratie in te lezen in de aangifte. Je kunt wel verder met de aangifte door de gegevens zelf in te vullen. Onze excuses voor het ongemak.',);
          }
        })
          .catch((error) => {
            console.log(error)
            if (error.error) {
              if (JSON.stringify(error.error).includes('RGS-code is leeg')) {
                this.toastService.showErrorMessage('Het is helaas niet gelukt de administratie in te lezen in de aangifte. De foutmelding is als volgt: ' + error.error + '. Je kunt wel verder met de aangifte door de gegevens zelf in te vullen. Onze excuses voor het ongemak.');
              }
              if (JSON.stringify(error.error).includes('Unauthorized access')) {
                this.toastService.showErrorMessage('Het is helaas niet gelukt de administratie in te lezen in de aangifte. Je hebt geen toegang tot deze administratie.');
              }
              else {
                  this.toastService.showErrorMessage('Het is helaas niet gelukt de administratie in te lezen in de aangifte. De administratie is namelijk niet (volledig) RGS-ready. Pas de RGS-codes in de administratie eerst aan of ga verder met de aangifte door de gegevens zelf in te vullen. Onze excuses voor het ongemak.');
              }
            } else {
              this.toastService.showErrorMessage('Helaas is er een technisch probleem waardoor de koppeling niet succesvol is verlopen. Wij werken hard aan een oplossing. Je kunt wel verder met de aangifte door de gegevens zelf in te vullen. Onze excuses voor het ongemak.',);
            }
          });
      }
    } else {
      this.toastService.showErrorMessage('Het is helaas niet gelukt de administratie in te lezen in de aangifte. Je kunt wel verder met de aangifte door de gegevens zelf in te vullen. Onze excuses voor het ongemak.',);
    }
    this.showSpinner = true;
    setTimeout(() => {
      this.showSpinner = false;
    }, 1000);
  }


  saveForm(): void {
    this.sstAdminIdFg.updateValueAndValidity()
    const administratieId = this.sstAdminIdFg.getRawValue().administrationId;
    this.corporateTaxEntity.aangifte = {
      ...this.corporateTaxEntity.aangifte,
      balansEnResultatenrekening: {
        ...this.corporateTaxEntity.aangifte?.balansEnResultatenrekening,
        administratieId: administratieId,
      },
    } as VpbAangifte;
    this.dataVpbService.updateCorporateTaxEntity(this.corporateTaxEntity);
  }

  removeBalansActivaPassiva(corporateTaxEntity: CorporateTaxEntity): void {
    if (corporateTaxEntity.aangifte) {
      if (corporateTaxEntity.aangifte.balansEnResultatenrekening?.goodwill) {
        corporateTaxEntity.aangifte.balansEnResultatenrekening.goodwill = undefined;
      }
      if (corporateTaxEntity.aangifte.balansEnResultatenrekening?.overigeImmaterieleActiva) {
        corporateTaxEntity.aangifte.balansEnResultatenrekening.overigeImmaterieleActiva = undefined;
      }
      if (corporateTaxEntity.aangifte.balansEnResultatenrekening?.gebouwenEnTerreinen) {
        corporateTaxEntity.aangifte.balansEnResultatenrekening.gebouwenEnTerreinen = undefined;
      }
      if (corporateTaxEntity.aangifte.balansEnResultatenrekening?.machinesEnInstallaties) {
        corporateTaxEntity.aangifte.balansEnResultatenrekening.machinesEnInstallaties = undefined;
      }
      if (corporateTaxEntity.aangifte.balansEnResultatenrekening?.andereVasteActiva) {
        corporateTaxEntity.aangifte.balansEnResultatenrekening.andereVasteActiva = undefined;
      }
      if (corporateTaxEntity.aangifte.balansEnResultatenrekening?.deelnemingen) {
        corporateTaxEntity.aangifte.balansEnResultatenrekening.deelnemingen = undefined;
      }
      if (corporateTaxEntity.aangifte.balansEnResultatenrekening?.vorderingenLangGroepsmaatschappijen) {
        corporateTaxEntity.aangifte.balansEnResultatenrekening.vorderingenLangGroepsmaatschappijen = undefined;
      }
      if (corporateTaxEntity.aangifte.balansEnResultatenrekening?.vorderingenLangParticipanten) {
        corporateTaxEntity.aangifte.balansEnResultatenrekening.vorderingenLangParticipanten = undefined;
      }
      if (corporateTaxEntity.aangifte.balansEnResultatenrekening?.overigeFinancieelVasteActiva) {
        corporateTaxEntity.aangifte.balansEnResultatenrekening.overigeFinancieelVasteActiva = undefined;
      }
      if (corporateTaxEntity.aangifte.balansEnResultatenrekening?.voorraden) {
        corporateTaxEntity.aangifte.balansEnResultatenrekening.voorraden = undefined;
      }
      if (corporateTaxEntity.aangifte.balansEnResultatenrekening?.onderhandenWerk) {
        corporateTaxEntity.aangifte.balansEnResultatenrekening.onderhandenWerk = undefined;
      }
      if (corporateTaxEntity.aangifte.balansEnResultatenrekening?.vorderingDebiteuren) {
        corporateTaxEntity.aangifte.balansEnResultatenrekening.vorderingDebiteuren = undefined;
      }
      if (corporateTaxEntity.aangifte.balansEnResultatenrekening?.vorderingenOmzetbelasting) {
        corporateTaxEntity.aangifte.balansEnResultatenrekening.vorderingenOmzetbelasting = undefined;
      }
      if (corporateTaxEntity.aangifte.balansEnResultatenrekening?.vorderingenKortGroepsmaatschappijen) {
        corporateTaxEntity.aangifte.balansEnResultatenrekening.vorderingenKortGroepsmaatschappijen = undefined;
      }
      if (corporateTaxEntity.aangifte.balansEnResultatenrekening?.vorderingenKortParticipanten) {
        corporateTaxEntity.aangifte.balansEnResultatenrekening.vorderingenKortParticipanten = undefined;
      }
      if (corporateTaxEntity.aangifte.balansEnResultatenrekening?.vorderingenOverig) {
        corporateTaxEntity.aangifte.balansEnResultatenrekening.vorderingenOverig = undefined;
      }
      if (corporateTaxEntity.aangifte.balansEnResultatenrekening?.effecten) {
        corporateTaxEntity.aangifte.balansEnResultatenrekening.effecten = undefined;
      }
      if (corporateTaxEntity.aangifte.balansEnResultatenrekening?.liquideMiddelen) {
        corporateTaxEntity.aangifte.balansEnResultatenrekening.liquideMiddelen = undefined;
      }
      if (corporateTaxEntity.aangifte.balansEnResultatenrekening?.gestortEnOpgevraagdKapitaal) {
        corporateTaxEntity.aangifte.balansEnResultatenrekening.gestortEnOpgevraagdKapitaal = undefined;
      }
      if (corporateTaxEntity.aangifte.balansEnResultatenrekening?.informeelKapitaal) {
        corporateTaxEntity.aangifte.balansEnResultatenrekening.informeelKapitaal = undefined;
      }
      if (corporateTaxEntity.aangifte.balansEnResultatenrekening?.agioreserve) {
        corporateTaxEntity.aangifte.balansEnResultatenrekening.agioreserve = undefined;
      }
      if (corporateTaxEntity.aangifte.balansEnResultatenrekening?.winstreserves) {
        corporateTaxEntity.aangifte.balansEnResultatenrekening.winstreserves = undefined;
      }
      if (corporateTaxEntity.aangifte.balansEnResultatenrekening?.kostenEgalisatieReserve) {
        corporateTaxEntity.aangifte.balansEnResultatenrekening.kostenEgalisatieReserve = undefined;
      }
      if (corporateTaxEntity.aangifte.balansEnResultatenrekening?.herinvesteringsReserve) {
        corporateTaxEntity.aangifte.balansEnResultatenrekening.herinvesteringsReserve = undefined;
      }
      if (corporateTaxEntity.aangifte.balansEnResultatenrekening?.specificatieHerinvesteringsreserve) {
        corporateTaxEntity.aangifte.balansEnResultatenrekening.specificatieHerinvesteringsreserve = undefined;
      }
      if (corporateTaxEntity.aangifte.balansEnResultatenrekening?.compartimenteringsreserve) {
        corporateTaxEntity.aangifte.balansEnResultatenrekening.compartimenteringsreserve = undefined;
      }
      if (corporateTaxEntity.aangifte.balansEnResultatenrekening?.overigeReserves) {
        corporateTaxEntity.aangifte.balansEnResultatenrekening.overigeReserves = undefined;
      }
      if (corporateTaxEntity.aangifte.balansEnResultatenrekening?.garantieVoorzieningen) {
        corporateTaxEntity.aangifte.balansEnResultatenrekening.garantieVoorzieningen = undefined;
      }
      if (corporateTaxEntity.aangifte.balansEnResultatenrekening?.pensioenVoorzieningen) {
        corporateTaxEntity.aangifte.balansEnResultatenrekening.pensioenVoorzieningen = undefined;
      }
      if (corporateTaxEntity.aangifte.balansEnResultatenrekening?.oudedagsVoorzieningen) {
        corporateTaxEntity.aangifte.balansEnResultatenrekening.oudedagsVoorzieningen = undefined;
      }
      if (corporateTaxEntity.aangifte.balansEnResultatenrekening?.overigeVoorzieningen) {
        corporateTaxEntity.aangifte.balansEnResultatenrekening.overigeVoorzieningen = undefined;
      }
      if (corporateTaxEntity.aangifte.balansEnResultatenrekening?.schuldenLanglopendConvertibles) {
        corporateTaxEntity.aangifte.balansEnResultatenrekening.schuldenLanglopendConvertibles = undefined;
      }
      if (corporateTaxEntity.aangifte.balansEnResultatenrekening?.schuldenLanglopendObligaties) {
        corporateTaxEntity.aangifte.balansEnResultatenrekening.schuldenLanglopendObligaties = undefined;
      }
      if (corporateTaxEntity.aangifte.balansEnResultatenrekening?.schuldenLanglopendGroepsmaatschappijen) {
        corporateTaxEntity.aangifte.balansEnResultatenrekening.schuldenLanglopendGroepsmaatschappijen = undefined;
      }
      if (corporateTaxEntity.aangifte.balansEnResultatenrekening?.schuldenLanglopendParticipanten) {
        corporateTaxEntity.aangifte.balansEnResultatenrekening.schuldenLanglopendParticipanten = undefined;
      }
      if (corporateTaxEntity.aangifte.balansEnResultatenrekening?.schuldenLanglopendKredietinstellingen) {
        corporateTaxEntity.aangifte.balansEnResultatenrekening.schuldenLanglopendKredietinstellingen = undefined;
      }
      if (corporateTaxEntity.aangifte.balansEnResultatenrekening?.schuldenLanglopendOverig) {
        corporateTaxEntity.aangifte.balansEnResultatenrekening.schuldenLanglopendOverig = undefined;
      }
      if (corporateTaxEntity.aangifte.balansEnResultatenrekening?.crediteuren) {
        corporateTaxEntity.aangifte.balansEnResultatenrekening.crediteuren = undefined;
      }
      if (corporateTaxEntity.aangifte.balansEnResultatenrekening?.schuldenOmzetbelasting) {
        corporateTaxEntity.aangifte.balansEnResultatenrekening.schuldenOmzetbelasting = undefined;
      }
      if (corporateTaxEntity.aangifte.balansEnResultatenrekening?.schuldenLoonheffingen) {
        corporateTaxEntity.aangifte.balansEnResultatenrekening.schuldenLoonheffingen = undefined;
      }
      if (corporateTaxEntity.aangifte.balansEnResultatenrekening?.schuldenKortlopendGroepsmaatschappijen) {
        corporateTaxEntity.aangifte.balansEnResultatenrekening.schuldenKortlopendGroepsmaatschappijen = undefined;
      }
      if (corporateTaxEntity.aangifte.balansEnResultatenrekening?.schuldenKortlopendParticipanten) {
        corporateTaxEntity.aangifte.balansEnResultatenrekening.schuldenKortlopendParticipanten = undefined;
      }
      if (corporateTaxEntity.aangifte.balansEnResultatenrekening?.schuldenKortlopendOverig) {
        corporateTaxEntity.aangifte.balansEnResultatenrekening.schuldenKortlopendOverig = undefined;
      }
      setTimeout(()=> {
        this.dataVpbService.updateCorporateTaxEntity(corporateTaxEntity as CorporateTaxEntity);
      },500)
    }
  }

  private executeBalanceBaseProvider(entity: IncomeTaxEntity| CorporateTaxEntity, partnerBoolean: boolean, adminId: string) {
    const kolommmenBalans = this.pmClient.postBalanceProviderBalances(String(this.clientEntity.sstRelationId),String(this.externalProviderId), adminId, partnerBoolean,entity);
    kolommmenBalans.subscribe({
      next: (entity)=>{
          this.corporateTaxEntity = entity as CorporateTaxEntity;
      },
      error:() => {
        this.toastService.showErrorMessage(
          'Het is helaas niet gelukt de administratie in te lezen in de aangifte. Je kunt wel verder met de aangifte door de gegevens zelf in te vullen. Onze excuses voor het ongemak.',
        );
      },
      complete: () => {
        this.saveForm()
        this.toastService.showSuccessMessage(`De administratie ${this.corporateTaxEntity.aangifte?.boekjaar.einddatum?.getFullYear()} van '${this.sstAdminIdFg.get('adminName')?.value}' is succesvol ingelezen.`);
      }
    })
  }

  private checkExterneKoppeling(gekoppeldeAdmin: AdminEntity) {
    if (gekoppeldeAdmin.providerId) {
      this.externalProviderId=gekoppeldeAdmin.providerId;
      this.pmClient.getIdentityProviderId(String(gekoppeldeAdmin.providerId)).subscribe({
        next: (result) => {
          this.loginStatus = result['authenticated'];
        },
        error: (error) => {
          this.loginStatus=false
          this.toastService.showWarningMessage(error);
        },
        complete: () => {
          console.log('complete ');
        },
      })
      return true;
    }
    return false
  }

  public goToExternalProvider() {
    const selectedProviderId = this.externalProviderId
    this.pmClient.getBalanceProvider(selectedProviderId).pipe(catchError((error) => {
        return throwError(error);
      }),
    ).subscribe({
      next: (results: BalanceProvider) => {
        this.pmClient.getIdentityProviders(results.id).subscribe({
          next: (singleResult) =>{
            console.log(singleResult.uri)
            window.open(singleResult.uri,"_blank")
          }
        })
      },
      error: (error) => {
        console.log(error);
        this.toastService.showWarningMessage(error);
      },
      complete: () => {
        console.log('complete');
      },
    });
  }
}
